import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  subDays,
  format,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  subMonths,
  subYears,
  parse,
} from 'date-fns';
import { DateRange } from 'react-day-picker';
import { useDashboardSlice } from '../slice';

type SortPayload =
  | { filter: string }
  | { startDate: string; endDate: string }
  | null;

interface GaOverviewMetrics {
  totalUsers: number;
  newUsers: number;
  sessions: number;
  pageViews: number;
  averageSessionDuration: number;
  bounceRate: number;
}

interface TrendPoint {
  dateLabel: string;
  dateValue: string;
  sessions: number;
  totalUsers: number;
  newUsers: number;
  pageViews: number;
}

interface SourceRow {
  source: string;
  sessions: number;
  newUsers: number;
  totalUsers: number;
}

interface TopPageRow {
  path: string;
  views: number;
  avgDurationSeconds: number;
  bounceRate: number;
}

interface CountryRow {
  country: string;
  users: number;
}

interface DeviceRow {
  device: string;
  users: number;
}

interface AnalyticsContextValue {
  selectedFilter: string;
  setSelectedFilter: (value: string) => void;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  payload: SortPayload;
  baseCurrency: {
    code: string;
    symbol: string;
  };
  isCustomRangePending: boolean;
  refreshGaData: () => void;
  loadActiveUsersHistory: (range: HistoryRange) => void;
  activeUsersHistoryRange: HistoryRange | null;
  users: {
    data: any;
    isLoading: boolean;
  };
  orders: {
    data: any;
    isLoading: boolean;
  };
  downloads: {
    data: any;
    isLoading: boolean;
  };
  transactions: {
    data: any;
    isLoading: boolean;
  };
  ga: {
    overview: {
      metrics: GaOverviewMetrics | null;
      isLoading: boolean;
      error: unknown;
    };
    trend: {
      data: TrendPoint[];
      isLoading: boolean;
      error: unknown;
    };
    sources: {
      data: SourceRow[];
      isLoading: boolean;
      error: unknown;
    };
    topPages: {
      data: TopPageRow[];
      isLoading: boolean;
      error: unknown;
    };
    demographics: {
      countries: CountryRow[];
      devices: DeviceRow[];
      isLoading: boolean;
      error: unknown;
    };
    activeUsers: {
      realtime: {
        total: number;
        timeline: { minute: string; activeUsers: number; events: number }[];
        isLoading: boolean;
        error: unknown;
      };
      history: {
        timeline: { timestamp: string; activeUsers: number }[];
        summary: {
          total: number;
          max: number;
          average: number;
        };
        granularity: 'hour' | 'day' | null;
        isLoading: boolean;
        error: unknown;
      };
    };
    integration: {
      isConfigured: boolean;
      message?: string;
    };
  };
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

type HistoryRange = {
  startDate: string;
  endDate: string;
};
const formatDateString = (date: Date) => format(date, 'yyyy-MM-dd');

const resolveDateRange = (
  filter: string,
  range: DateRange,
): { startDate: string; endDate: string } | null => {
  const today = new Date();

  if (filter === 'custom') {
    if (range?.from && range?.to) {
      return {
        startDate: formatDateString(range.from),
        endDate: formatDateString(range.to),
      };
    }
    return null;
  }

  if (filter === 'Today') {
    return {
      startDate: formatDateString(today),
      endDate: formatDateString(today),
    };
  }

  if (filter === 'Yesterday') {
    const yesterday = subDays(today, 1);
    return {
      startDate: formatDateString(yesterday),
      endDate: formatDateString(yesterday),
    };
  }

  if (filter === 'This week') {
    const start = startOfWeek(today, { weekStartsOn: 1 });
    const end = endOfWeek(today, { weekStartsOn: 1 });
    const cappedEnd = end > today ? today : end;
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(cappedEnd),
    };
  }

  if (filter === 'Last week') {
    const start = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
    const end = endOfWeek(start, { weekStartsOn: 1 });
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(end),
    };
  }

  const relativeDays: Record<string, number> = {
    'Last 7 days': 6,
    'Last 14 days': 13,
    'Last 28 days': 27,
    'Last 30 days': 29,
    'Last 60 days': 59,
    'Last 90 days': 89,
  };

  if (relativeDays[filter] !== undefined) {
    const start = subDays(today, relativeDays[filter]);
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(today),
    };
  }

  if (filter === 'Last 12 Month') {
    const start = subMonths(today, 12);
    return {
      startDate: formatDateString(start),
      endDate: formatDateString(today),
    };
  }

  if (filter === 'This Year') {
    return {
      startDate: formatDateString(startOfYear(today)),
      endDate: formatDateString(today),
    };
  }

  if (filter === 'Last Year') {
    const lastYear = subYears(today, 1);
    return {
      startDate: formatDateString(startOfYear(lastYear)),
      endDate: formatDateString(endOfYear(lastYear)),
    };
  }

  // Default fallback: last 30 days
  const fallbackStart = subDays(today, 29);
  return {
    startDate: formatDateString(fallbackStart),
    endDate: formatDateString(today),
  };
};

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedFilter, setSelectedFilter] = useState('Last 7 days');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 6),
    to: new Date(),
  });
  const [activeUsersHistoryRange, setActiveUsersHistoryRange] =
    useState<HistoryRange | null>(null);

  const payload: SortPayload = useMemo(() => {
    if (selectedFilter === 'custom' && dateRange?.from && dateRange?.to) {
      return {
        startDate: dateRange.from.toISOString().slice(0, 10),
        endDate: dateRange.to.toISOString().slice(0, 10),
      };
    }

    if (selectedFilter !== 'custom') {
      return { filter: selectedFilter };
    }

    return null;
  }, [dateRange, selectedFilter]);

  const resolvedRange = useMemo(
    () => resolveDateRange(selectedFilter, dateRange),
    [selectedFilter, dateRange],
  );

  const {
    useGetUsersChartQuery,
    useGetOrdersChartQuery,
    useGetDownloadsChartQuery,
    useGetTransactionsChartQuery,
    useGetGaOverviewQuery,
    useGetGaPageViewsQuery,
    useGetGaAcquisitionQuery,
    useGetGaDemographicsQuery,
    useGetGaTopPagesQuery,
  } = useDashboardSlice();

  const [triggerUsers, usersResult] = useGetUsersChartQuery({});
  const [triggerOrders, ordersResult] = useGetOrdersChartQuery({});
  const [triggerDownloads, downloadsResult] = useGetDownloadsChartQuery({});
  const [triggerTransactions, transactionsResult] =
    useGetTransactionsChartQuery({});
  const [triggerGaOverview, gaOverviewResult] = useGetGaOverviewQuery();
  const [triggerGaPageViews, gaPageViewsResult] = useGetGaPageViewsQuery();
  const [triggerGaAcquisition, gaAcquisitionResult] =
    useGetGaAcquisitionQuery();
  const [triggerGaDemographics, gaDemographicsResult] =
    useGetGaDemographicsQuery();
  const [triggerGaTopPages, gaTopPagesResult] = useGetGaTopPagesQuery();
  const { useGetGaActiveUsersQuery, useGetGaActiveUsersHistoryQuery } =
    useDashboardSlice();
  const [triggerGaActiveUsers, gaActiveUsersResult] =
    useGetGaActiveUsersQuery();
  const [triggerGaActiveUsersHistory, gaActiveUsersHistoryResult] =
    useGetGaActiveUsersHistoryQuery();

  const triggerAll = useCallback(
    (requestPayload: SortPayload) => {
      if (!requestPayload) {
        return;
      }
      triggerUsers(requestPayload);
      triggerOrders(requestPayload);
      triggerDownloads(requestPayload);
      triggerTransactions(requestPayload);
    },
    [triggerUsers, triggerOrders, triggerDownloads, triggerTransactions],
  );

  const isCustomRangePending =
    selectedFilter === 'custom' && (!dateRange?.from || !dateRange?.to);

  useEffect(() => {
    triggerAll(payload);
  }, [payload, triggerAll]);

  const loadActiveUsersHistory = useCallback(
    (range: HistoryRange) => {
      if (!range?.startDate || !range?.endDate) return;
      setActiveUsersHistoryRange(range);
      triggerGaActiveUsersHistory(range);
    },
    [triggerGaActiveUsersHistory],
  );

  const refreshGaData = useCallback(() => {
    if (!resolvedRange) return;
    triggerGaOverview(resolvedRange);
    triggerGaPageViews(resolvedRange);
    triggerGaAcquisition(resolvedRange);
    triggerGaDemographics(resolvedRange);
    triggerGaTopPages(resolvedRange);
    triggerGaActiveUsers();
    const todayString = formatDateString(new Date());
    loadActiveUsersHistory({
      startDate: todayString,
      endDate: todayString,
    });
  }, [
    resolvedRange,
    triggerGaOverview,
    triggerGaPageViews,
    triggerGaAcquisition,
    triggerGaDemographics,
    triggerGaTopPages,
    triggerGaActiveUsers,
    loadActiveUsersHistory,
  ]);

  useEffect(() => {
    refreshGaData();
  }, [refreshGaData]);

  useEffect(() => {
    if (!resolvedRange) return;
    triggerGaOverview(resolvedRange);
    triggerGaPageViews(resolvedRange);
    triggerGaAcquisition(resolvedRange);
    triggerGaDemographics(resolvedRange);
    triggerGaTopPages(resolvedRange);
  }, [
    resolvedRange,
    triggerGaOverview,
    triggerGaPageViews,
    triggerGaAcquisition,
    triggerGaDemographics,
    triggerGaTopPages,
  ]);

  const resolvedBaseCurrency =
    ordersResult.data?.currency || transactionsResult.data?.currency;
  const baseCurrencyInfo = resolvedBaseCurrency
    ? resolvedBaseCurrency
    : { code: 'USD', symbol: '$' };

  const gaOverviewMetrics: GaOverviewMetrics | null = useMemo(() => {
    if (!gaOverviewResult.data?.response?.data) {
      return null;
    }
    const raw = gaOverviewResult.data.response.data;
    return {
      ...raw,
      averageSessionDuration: parseFloat(raw.averageSessionDuration ?? 0) || 0,
      bounceRate: parseFloat(raw.bounceRate ?? 0) || 0,
    };
  }, [gaOverviewResult.data]);

  const gaTrendData: TrendPoint[] = useMemo(() => {
    const rows = gaPageViewsResult.data?.rows ?? [];
    return rows.map(row => {
      const rawDate = row.dimensionValues?.[0]?.value ?? '';
      const parsedDate = rawDate
        ? parse(rawDate, 'yyyyMMdd', new Date())
        : new Date();
      const metrics = row.metricValues || [];
      return {
        dateLabel: format(parsedDate, 'MMM d'),
        dateValue: rawDate,
        sessions: Number(metrics[0]?.value || 0),
        totalUsers: Number(metrics[1]?.value || 0),
        newUsers: Number(metrics[2]?.value || 0),
        pageViews: Number(metrics[3]?.value || 0),
      };
    });
  }, [gaPageViewsResult.data]);

  const gaSourcesData: SourceRow[] = useMemo(() => {
    const rows = gaAcquisitionResult.data?.rows ?? [];
    return rows
      .map(row => ({
        source: row.dimensionValues?.[0]?.value || 'Unknown',
        sessions: Number(row.metricValues?.[0]?.value || 0),
        newUsers: Number(row.metricValues?.[1]?.value || 0),
        totalUsers: Number(row.metricValues?.[2]?.value || 0),
      }))
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 10);
  }, [gaAcquisitionResult.data]);

  const gaTopPagesData: TopPageRow[] = useMemo(() => {
    const rows = gaTopPagesResult.data?.rows ?? [];
    return rows
      .map(row => ({
        path: row.dimensionValues?.[0]?.value || '/',
        views: Number(row.metricValues?.[0]?.value || 0),
        avgDurationSeconds: Number(row.metricValues?.[1]?.value || 0),
        bounceRate: Number(row.metricValues?.[2]?.value || 0),
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);
  }, [gaTopPagesResult.data]);

  const demographicsAggregates = useMemo(() => {
    const rows = gaDemographicsResult.data?.rows ?? [];
    const countryMap = new Map<string, number>();
    const deviceMap = new Map<string, number>();

    rows.forEach(row => {
      const country = row.dimensionValues?.[0]?.value || 'Unknown';
      const device =
        row.dimensionValues?.[2]?.value?.toLowerCase() || 'unknown';
      const users = Number(row.metricValues?.[0]?.value || 0);

      countryMap.set(country, (countryMap.get(country) || 0) + users);
      deviceMap.set(device, (deviceMap.get(device) || 0) + users);
    });

    const countries: CountryRow[] = Array.from(countryMap.entries())
      .map(([country, users]) => ({ country, users }))
      .sort((a, b) => b.users - a.users)
      .slice(0, 10);

    const devices: DeviceRow[] = Array.from(deviceMap.entries())
      .map(([device, users]) => ({
        device: device.charAt(0).toUpperCase() + device.slice(1),
        users,
      }))
      .sort((a, b) => b.users - a.users);

    return { countries, devices };
  }, [gaDemographicsResult.data]);

  const contextValue = useMemo<AnalyticsContextValue>(() => {
    const gaOverviewError = gaOverviewResult.error || null;
    const gaTrendError = gaPageViewsResult.error || null;
    const gaSourceError = gaAcquisitionResult.error || null;
    const gaTopPagesError = gaTopPagesResult.error || null;
    const gaDemographicsError = gaDemographicsResult.error || null;
    const gaActiveUsersError = gaActiveUsersResult.error || null;

    const gaConfigMissing = [
      gaOverviewError,
      gaTrendError,
      gaSourceError,
      gaTopPagesError,
      gaDemographicsError,
      gaActiveUsersError,
    ].some((error: any) => error?.status === 400);

    const gaIntegrationStatus = {
      isConfigured: !gaConfigMissing,
      message: gaConfigMissing
        ? (gaOverviewError as any)?.data?.message ||
          'Google Analytics integration needs to be configured.'
        : undefined,
    };

    const historyResponse = gaActiveUsersHistoryResult.data?.response;
    const historyTimeline =
      historyResponse?.timeline?.map((item: any) => ({
        timestamp: item.timestamp,
        activeUsers: Number(item.activeUsers || 0),
      })) ?? [];
    const historySummaryRaw = historyResponse?.summary;
    const historySummary = {
      total: Number(historySummaryRaw?.total || 0),
      max: Number(historySummaryRaw?.max || 0),
      average: Number(historySummaryRaw?.average || 0),
    };
    const historyGranularityRaw = historyResponse?.granularity;
    const historyGranularity =
      historyGranularityRaw === 'hour' || historyGranularityRaw === 'day'
        ? historyGranularityRaw
        : null;

    return {
      selectedFilter,
      setSelectedFilter,
      dateRange,
      setDateRange,
      payload,
      baseCurrency: {
        code: baseCurrencyInfo?.code || 'USD',
        symbol: baseCurrencyInfo?.symbol || '$',
      },
      isCustomRangePending,
      refreshGaData,
      loadActiveUsersHistory,
      activeUsersHistoryRange,
      users: {
        data: usersResult.data,
        isLoading: usersResult.isLoading,
      },
      orders: {
        data: ordersResult.data,
        isLoading: ordersResult.isLoading,
      },
      downloads: {
        data: downloadsResult.data,
        isLoading: downloadsResult.isLoading,
      },
      transactions: {
        data: transactionsResult.data,
        isLoading: transactionsResult.isLoading,
      },
      ga: {
        overview: {
          metrics: gaOverviewMetrics,
          isLoading: gaOverviewResult.isLoading,
          error: gaOverviewError,
        },
        trend: {
          data: gaTrendData,
          isLoading: gaPageViewsResult.isLoading,
          error: gaTrendError,
        },
        sources: {
          data: gaSourcesData,
          isLoading: gaAcquisitionResult.isLoading,
          error: gaSourceError,
        },
        topPages: {
          data: gaTopPagesData,
          isLoading: gaTopPagesResult.isLoading,
          error: gaTopPagesError,
        },
        demographics: {
          countries: demographicsAggregates.countries,
          devices: demographicsAggregates.devices,
          isLoading: gaDemographicsResult.isLoading,
          error: gaDemographicsError,
        },
        activeUsers: {
          realtime: {
            total:
              (gaActiveUsersResult.data?.response?.totalActiveUsers ?? 0) || 0,
            timeline:
              gaActiveUsersResult.data?.response?.timeline?.map(
                (item: any) => ({
                  minute: item.minute,
                  activeUsers: Number(item.activeUsers || 0),
                  events: Number(item.events || 0),
                }),
              ) ?? [],
            isLoading: gaActiveUsersResult.isLoading,
            error: gaActiveUsersError,
          },
          history: {
            timeline: historyTimeline,
            summary: historySummary,
            granularity: historyGranularity,
            isLoading: gaActiveUsersHistoryResult.isLoading,
            error: gaActiveUsersHistoryResult.error || null,
          },
        },
        integration: gaIntegrationStatus,
      },
    };
  }, [
    selectedFilter,
    setSelectedFilter,
    dateRange,
    setDateRange,
    payload,
    baseCurrencyInfo?.code,
    baseCurrencyInfo?.symbol,
    isCustomRangePending,
    refreshGaData,
    loadActiveUsersHistory,
    activeUsersHistoryRange,
    usersResult.data,
    usersResult.isLoading,
    ordersResult.data,
    ordersResult.isLoading,
    downloadsResult.data,
    downloadsResult.isLoading,
    transactionsResult.data,
    transactionsResult.isLoading,
    gaOverviewMetrics,
    gaOverviewResult.isLoading,
    gaOverviewResult.error,
    gaTrendData,
    gaPageViewsResult.isLoading,
    gaPageViewsResult.error,
    gaSourcesData,
    gaAcquisitionResult.isLoading,
    gaAcquisitionResult.error,
    gaTopPagesData,
    gaTopPagesResult.isLoading,
    gaTopPagesResult.error,
    demographicsAggregates.countries,
    demographicsAggregates.devices,
    gaDemographicsResult.isLoading,
    gaDemographicsResult.error,
    gaActiveUsersResult.data?.response?.totalActiveUsers,
    gaActiveUsersResult.data?.response?.timeline,
    gaActiveUsersResult.isLoading,
    gaActiveUsersResult.error,
    gaActiveUsersHistoryResult.data?.response,
    gaActiveUsersHistoryResult.isLoading,
    gaActiveUsersHistoryResult.error,
  ]);

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = () => {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    throw new Error(
      'useAnalyticsContext must be used within an AnalyticsProvider',
    );
  }
  return ctx;
};
